import React, {
  Fragment, useState, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from '@common/hooks/translation';
import { LIST_COUNT_PER_PAGE } from '@common/constants';
import SimpleDropDown from '@common/components/simpleDropdown';
import { useScreenMatch } from '@common/hooks/screenMatch';

import Pagination from '../pagination';
import SmartTableMobile from './mobile';

import './style.scss';

export default function SmartTable(props) {
  const {
    data,
    hasIcon,
    rowClick,
    className,
    columnConfig,
    activeRowOpen,
    headerMissing,
    activeRowContent,
    hasPagination,
    countPerPage,
    enablePageSizeSelector,
    pageSizeOptions,
    pageSize: controlledPageSize,
    onPageSizeChange,
    adaptive,
  } = props;
  const t = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSizeList = pageSizeOptions && pageSizeOptions.length ? pageSizeOptions : [10, 20, 30, 50];
  const [uncontrolledPageSize, setUncontrolledPageSize] = useState(countPerPage || LIST_COUNT_PER_PAGE);
  const [isPagionationSizeOpened, setIsPaginationSizeOpened] = useState(false);
  const effectivePageSize = (typeof controlledPageSize === 'number' ? controlledPageSize : uncontrolledPageSize) || LIST_COUNT_PER_PAGE;
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useScreenMatch('mobile');

  const [pageData, pageCount] = useMemo(() => {
    const safeData = Array.isArray(data) ? data : [];
    const perPageCount = effectivePageSize || LIST_COUNT_PER_PAGE;

    if (hasPagination && safeData.length > perPageCount) {
      return [
        safeData.slice((currentPage - 1) * perPageCount, currentPage * perPageCount),
        Math.ceil(safeData.length / perPageCount),
      ];
    }

    return [safeData, 1];
  }, [data, currentPage, hasPagination, effectivePageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  if (adaptive && isMobile) return <SmartTableMobile {...props} />;

  return (
    <div className={`smart-table-wrapper ${className || ''}`}>
      {!headerMissing && (
        <div className="smart-table-header-wrp">
          <div className="smart-table-header">
            {columnConfig.filter((item) => !item?.isHidden).map((item, index) => {
              const colName = typeof item.name === 'function'
                ? item.name()
                : t(item.name);

              return (
                <div
                  key={`${colName}-${index}`}
                  className={`smart-table-header-clm ${item.columnClassName || ''}`}
                >
                  <p className="smart-table-header-txt">{colName}</p>
                </div>

              );
            })}
          </div>
        </div>
      )}

      <div className="smart-table-body">
        {pageData.map((item, index) => {
          const rowActive = !!activeRowOpen?.(item, index);

          return (
            <Fragment key={index}>
              <div
                className={`smart-table-list-item ${rowActive ? 'active' : ''} ${index % 2 ? 's-t-even' : 's-t-odd'}`}
                onClick={(e) => rowClick && rowClick(e, item, index)}
              >
                {hasIcon && (
                  rowActive
                    ? <i className="s-t-row-toggle-icon global-icon icon-arrow-down5" />
                    : <i className="s-t-row-toggle-icon global-icon icon-arrow-right5" />
                )}
                {columnConfig.map((subItem, subIndex) => !subItem.isHidden && (
                  <div key={subIndex} className={`smart-table-list-clm ${subItem.rowClassName || ''}`}>
                    <div className="smart-table-list-clm-txt">
                      {subItem.row(item, index, rowActive)}
                    </div>
                  </div>
                ))}
              </div>

              {activeRowContent && item.ticketType !== 'deposit' && (
                <div className={`smart-table-list-active-row-content ${rowActive ? 'smart-table-row-open' : ''}`}>
                  {activeRowContent(item)}
                </div>
              )}
            </Fragment>
          );
        })}
      </div>

      {!!hasPagination && (
        <div className="smart-table-pagination">
          {enablePageSizeSelector && (
            <div className="page-size-selector">
              <SimpleDropDown
                setIsOpen={setIsOpen}
                className="page-size-dropdown"
                enableIcon={false}
                header={(
                  <span>
                    {`${effectivePageSize}/PAGE`}
                    {isOpen ? <i className="global-icon icon-thin-arrow-down page-selector-icon" /> : <i className="global-icon icon-thin-arrow-up page-selector-icon" />}
                  </span>
                )}
                openSection={isPagionationSizeOpened}
                onClose={() => setIsPaginationSizeOpened()}
                onOpen={() => setIsPaginationSizeOpened(true)}
              >
                <div className="page-size-options">
                  {pageSizeList.map((size) => (
                    <div
                      key={size}
                      className={`page-size-option ${size === effectivePageSize ? 'active' : ''}`}
                      onClick={() => {
                        if (typeof onPageSizeChange === 'function') onPageSizeChange(size);
                        if (typeof controlledPageSize !== 'number') setUncontrolledPageSize(size);
                        setCurrentPage(1);
                        setIsPaginationSizeOpened(false);
                      }}
                    >
                      <span>{size}</span>
                      {size === effectivePageSize ? <i className="global-icon icon-regular-checkmark checkmark" /> : ''}
                    </div>
                  ))}
                </div>
              </SimpleDropDown>
            </div>
          )}
          {pageCount > 1 && (
            <Pagination
              pageCount={pageCount}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}
    </div>
  );
}

SmartTable.propTypes = {
  hasIcon: PropTypes.bool,
  hasPagination: PropTypes.bool,
  countPerPage: PropTypes.number,
  data: PropTypes.array.isRequired,
  className: PropTypes.string,
  columnConfig: PropTypes.array.isRequired,
  rowClick: PropTypes.func,
  headerMissing: PropTypes.bool,
  activeRowOpen: PropTypes.func,
  activeRowContent: PropTypes.func,
  enablePageSizeSelector: PropTypes.bool,
  pageSizeOptions: PropTypes.array,
  adaptive: PropTypes.bool,
};
